import { type UserState } from '@carvana/core-auth';
import { DefaultLocation, LocationData } from '@carvana/merch-geolocation';
import {
  defaultLoadableState,
  LoadableState,
  useLoadableState,
  useSubsequentEffect
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { FavoriteVehicle } from '~/contracts/common';
import { DisplayableFavorite } from '~/hooks/home/useGetDisplayableFavorites';
import type { DeepPartial } from '~/types';
import EventManager, { useEventTrigger } from '~/utilities/EventManager';

export type UserContextType = {
  displayableFavorites: LoadableState<DisplayableFavorite[]>;
  favoriteChangeFlag: number;
  favorites: LoadableState<FavoriteVehicle[]>;
  location: LoadableState<LocationData>;
  logInFlag: number;
  logOutFlag: number;
  triggerEvent: EventManager;
  triggerFetchFavorites: () => void;
  triggerFetchUserProfile: () => void;
  userState: LoadableState<Partial<UserState>>;
};

export const defaultUserContext: UserContextType = {
  displayableFavorites: defaultLoadableState<DisplayableFavorite[]>([]),
  favoriteChangeFlag: 0,
  favorites: defaultLoadableState<FavoriteVehicle[]>([]),
  location: defaultLoadableState<LocationData>(DefaultLocation),
  logInFlag: 0,
  logOutFlag: 0,
  triggerEvent: new EventManager(false),
  triggerFetchFavorites() {},
  triggerFetchUserProfile() {},
  userState: defaultLoadableState<Partial<UserState>>({})
};

const UserContext = createContext<UserContextType>(defaultUserContext);

export type OwnProps = PropsWithChildren<{
  location: LocationData;
  userState?: Partial<UserState>;
}>;

export function UserContextProvider(props: OwnProps) {
  const displayableFavorites = useLoadableState<DisplayableFavorite[]>([]);
  const favorites = useLoadableState<FavoriteVehicle[]>([]);
  const location = useLoadableState<LocationData>(props.location);
  const userState = useLoadableState<Partial<UserState>>(props.userState ?? {});

  const eventManager = useMemo(() => new EventManager(true), []);
  const [favoriteChangeFlag, setFavoritesTrigger] = useState(
    defaultUserContext.favoriteChangeFlag
  );
  const addFavoriteFlag = useEventTrigger(eventManager, 'favoriteAdd');
  const removeFavoriteFlag = useEventTrigger(eventManager, 'favoriteRemove');
  const remoteUserRequestFlag = useEventTrigger(eventManager, 'logIn');
  const remoteLogOutFlag = useEventTrigger(eventManager, 'logOut');
  const [localUserRequestFlag, setLocalUserRequestFlag] = useState(0);

  useSubsequentEffect(() => {
    setFavoritesTrigger(addFavoriteFlag - removeFavoriteFlag);
  }, [addFavoriteFlag, removeFavoriteFlag]);

  const triggerFetchUserProfile = useCallback(() => {
    setLocalUserRequestFlag(Date.now());
  }, []);

  const triggerFetchFavorites = useCallback(() => {
    setFavoritesTrigger(Date.now());
  }, []);

  const value = useMemo<UserContextType>(
    () => ({
      displayableFavorites,
      favoriteChangeFlag,
      favorites,
      location,
      logInFlag: remoteUserRequestFlag - localUserRequestFlag, // Using subtraction to avoid integer overflow
      logOutFlag: remoteLogOutFlag,
      triggerEvent: eventManager,
      triggerFetchFavorites,
      triggerFetchUserProfile,
      userState
    }),
    [
      displayableFavorites,
      eventManager,
      favoriteChangeFlag,
      favorites,
      localUserRequestFlag,
      location,
      remoteLogOutFlag,
      remoteUserRequestFlag,
      triggerFetchFavorites,
      triggerFetchUserProfile,
      userState
    ]
  );

  return (
    <UserContext.Provider value={value}>{props.children}</UserContext.Provider>
  );
}

export const generateUserContext = (
  customState: DeepPartial<UserContextType>
): UserContextType =>
  deepmerge<UserContextType, DeepPartial<UserContextType>>(
    defaultUserContext,
    customState
  );

export function useFavorites(): FavoriteVehicle[] {
  return useContext(UserContext).favorites.data;
}

export function useFavoritesIds(): number[] {
  const favorites = useFavorites();

  return useMemo(
    () => favorites.map(favorite => favorite.vehicleId),
    [favorites]
  );
}

export function useIsLoggedIn(): boolean {
  return Boolean(useContext(UserContext).userState.data?.isLoggedIn);
}

export function useGetUser(): Partial<UserState['user']> {
  return useContext(UserContext).userState.data.user ?? {};
}

export function useUserEmail(): string {
  return useContext(UserContext).userState.data.user?.email ?? '';
}

export function useUserId(): string {
  return useContext(UserContext).userState.data.user?.user_id ?? '';
}

export function useUserLocation(): LocationData {
  return useContext(UserContext).location.data ?? DefaultLocation;
}

UserContextProvider.displayName = 'UserContextProvider';

export default UserContext;
